import cloneDeep from "lodash/cloneDeep";
import { useEffect, useMemo, useRef, useState } from "react";
import { contextMenu } from "react-contexify";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { FormattedMessage } from "react-intl";
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ListIcon from '@mui/icons-material/List';
import Box from "@mui/material/Box";
import {
  Body,
  Cell,
  Header,
  HeaderCell,
  HeaderRow,
  Row,
  Table,
  Footer,
  FooterRow,
  FooterCell,
} from '@table-library/react-table-library/table';
import { useTheme } from "@table-library/react-table-library/theme";
import { TreeExpandClickTypes, useTree } from '@table-library/react-table-library/tree';
import ProjectDnDContextProvider from "../../context/ProjectDnDContext";
import {
  DateValueEditor,
  DurationValueEditor,
  NumberValueEditor,
  StatusValueEditor,
  StringValueEditor,
} from "../../pages/ProjectTaskPage/ValueEditors";
import EditorContainer from "../common/EditorContainer";
import DateEditor from "../common/EditorContainer/DateEditor";
import DurationPopper from "../common/EditorContainer/DurationEditor/DurationPopper";
import StringEditor from "../common/EditorContainer/StringEditor";
import CellTreeHoverMore from "./CellTreeHoverMore";
import ColumnModal from "./ColumnModal/ColumnModal";
import ColumnVisibilitySelector from "./ColumnVisibilitySelector";
import {
  buildTree,
  findNodeById,
  renderDateCellValue,
  TaskAction,
  TaskAddPosition,
  TimeLogAction,
} from "./helper";
import { ColumnTypes, StandardColumnIds } from "./helpers/columnUtils";
import useTableTheme from './hooks/useTableTheme';
import NewTaskRow from "./NewTaskRow";
import ProjectButton from "./ProjectButton";
import TaskContextMenu from "./TaskContextMenu";
import UserField from "./TaskDetails/UserField";

const getProjectFieldColumnWidth = (field) => {
  switch (field.type) {
    case ColumnTypes.TIME_REPORT: return "150px"; // Duration
    case ColumnTypes.STATUS: return "minmax(50px,150px)";
    case ColumnTypes.DATE: return "150px";
    case ColumnTypes.NUMBER: return "150px";
    case ColumnTypes.STRING: return "minmax(100px,auto)";

    default:
      console.warn("** unhandled type: " + field.type);
      return "auto";
  }
};

const createColumns = (projectFields, viewColumnIds) => {
  if (!Array.isArray(projectFields) || projectFields.length === 0) {
    return [];
  }

  const orderMap = new Map(viewColumnIds.map((id, index) => [id, index]));

  const filteredAndSortedFields = projectFields
    .filter(field => orderMap.has(field.fieldId))
    .sort((a, b) => orderMap.get(a.fieldId) - orderMap.get(b.fieldId));

  const columns = filteredAndSortedFields.map((projectField) => {
    const { fieldId, name, type, jsonFieldSettings } = projectField;

    let field = { ...projectField };

    if (jsonFieldSettings) {
      try {
        const settings = JSON.parse(jsonFieldSettings);
        field = { ...field, settings };
      } catch (error) {
        console.error(`Error parsing settings for projectField: ${field}`, error);
      }
    }

    return {
      fieldId,
      label: name,
      renderCell: (item, onChange, enableEdit, inline, disabled, onValidationChange) =>
        renderProjectFieldCell(enableEdit, field, item, onChange, inline, disabled, onValidationChange),
      type,
      width: getProjectFieldColumnWidth(field),
    };
  });

  return columns;
};

const createNodes = (tasksList) => {
  if (!Array.isArray(tasksList) || tasksList.length === 0) {
    return [];
  }

  return buildTree(tasksList);
};

const renderProjectFieldCell = (enableEdit, field, item, onChange, inline, disabled, onValidationChange) => {
  const displayValue = item.values.find(n => n.fieldId === field.fieldId);

  return renderValueCell(enableEdit, field, displayValue, onChange, inline, disabled, onValidationChange);
};

const renderValueCell = (enableEdit, field, value, onChange, inline, disabled, onValidationChange) => {
  const commonProps = { disabled, enableEdit, field, inline, onUpdateTaskValue: onChange, value };

  switch (field.type) {
    case ColumnTypes.TIME_REPORT: return <DurationValueEditor {...commonProps} sx={{ justifyContent: (inline ? "flex-end" : "flex-start") }} />;
    case ColumnTypes.STATUS: return <StatusValueEditor {...commonProps} hideUnassigned={inline} />;
    case ColumnTypes.DATE: return renderDateValueCell(enableEdit, field, value, onChange, onValidationChange);
    case ColumnTypes.NUMBER: return <NumberValueEditor {...commonProps} />;
    case ColumnTypes.STRING: return <StringValueEditor {...commonProps} />;

    default: return "Type: " + field.type + ":" + value || "No Value"
  }
};

const renderDateValueCell = (enableEdit, field, value, onChange, onValidationChange) => {
  return enableEdit
    ? <DateValueEditor field={field} value={value} onUpdateTaskValue={onChange} onValidationChange={(valid) => onValidationChange?.(field.fieldId, valid)} />
    : renderDateCellValue(value);
};

const dateEditorTextInputProps = {
  disableUnderline: true,
  endAdornment: null,
  startAdornment: null,
  placeholder: null,
  sx: {
    '&.MuiInputBase-root': {
      padding: 0,
    },
    '&.MuiInputBase-root.MuiInput-root': {
      padding: 0,
      "&.Mui-error": {
        color: /* theme.palette.error.dark */ "red",
      },
    },
    //width: "100%",
    backgroundColor: '#ffffff',
    textAlign: 'left',
    cursor: "text",
    padding: '4px',
    fontSize: '14px',
    minHeight: '1.5em',
    color: 'rgba(0, 0, 0, 0.87)',
    border: '1px solid rgba(0, 0, 0, 0)',
    borderRadius: "4px",
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
    '&.Mui-focused': {
      border: '1px solid rgba(0, 0, 0, 0.5)',
    },
  },
};



const TableView = ({
  disabled,
  enableEdit,
  onProjectUpdate,
  onTaskAction,
  onTimeLogAction,
  project,
  tasks,
  userLookup,
  viewColumnIds,
}) => {
  const resizer = { minWidth: 100, resizerHighlight: "lightgray" };
  const TASK_CONTEXT_MENU_ID = "task_context_menu";

  // Ref to store previous column ids, avoids unnecessary re-renders
  const previousColumnIdsRef = useRef(new Set());

  const [columns, setColumns] = useState();
  const [contextMenuTask, setContextMenuTask] = useState();
  const [nodes, setNodes] = useState([]);
  const [showColumnEditor, setShowColumnEditor] = useState(false);
  const [visibleColumnIds, setVisibleColumnIds] = useState(viewColumnIds || []);

  const data = { nodes };

  const tableTheme = useTableTheme(columns, disabled, visibleColumnIds);
  const theme = useTheme(tableTheme);

  const tree = useTree(
    data,
    {
      onChange: (action, state) => { },
    },
    {
      clickType: TreeExpandClickTypes.ButtonClick,
      treeIcon: {
        margin: "2px",
        iconDefault: <ChevronRightIcon sx={{ opacity: 0, marginTop: "5px" }} fontSize="8px" />,
        iconRight: <ChevronRightIcon sx={{ marginTop: "5px" }} fontSize="8px" />,
        iconDown: <ExpandMoreIcon sx={{ marginTop: "5px" }} fontSize="8px" />,
      },
    }
  );

  const calcDropData = ({ pos, target }) => {
    let newPos = pos;
    let { taskId, parentTaskId } = target;

    if (pos === TaskAddPosition.AFTER) {
      var isExpanded = tree.state.ids.includes(taskId);
      if (isExpanded) {
        var targetNode = findNodeById(nodes, taskId);
        var hasChildren = !!targetNode.nodes;
        if (hasChildren) {
          newPos = TaskAddPosition.BEFORE;
          parentTaskId = taskId;
          taskId = targetNode.nodes[0].id;
        }
      }
    }

    return { pos: newPos, parentTaskId, taskId };
  };

  const createEmptyTask = (projectId, name) => ({
    assignments: [],
    description: "",
    endDate: null,
    id: 0,
    linkedFolderId: null,
    name: name || "",
    parentTaskId: null,
    projectId,
    projectTaskNo: 0,
    startDate: null,
    taskPropertyGroupId: null,
    taskWorkflowId: null,
    timestamp: null,
    values: [],
  });

  const handleButtonAction = ({ action, ...rest }) => {
    if (!onTaskAction) {
      console.warn("** missing onTaskAction");
      return;
    }

    const { projectId } = project;

    let data = {};

    switch (action) {
      case TaskAction.ADD:
        data = {
          parentTaskId: null,
          insertAfterTaskId: null,
          task: createEmptyTask(projectId),
        };
        break;

      case TaskAction.EDIT:
        const { task } = rest;
        data = {
          task: removeTreeProperties(cloneDeep(task)),
          taskId: task.id,
        };
        break;

      default:
        console.warn("** unhandled action", { action });
        return;
    };

    onTaskAction({ action, columns, data });
  };

  const handleDetailsButtonClick = (item, event) => {
    event.stopPropagation();
    handleButtonAction({ action: TaskAction.EDIT, task: item });
  };

  const handleDrop = ({ pos, source, target }) => {
    var { pos: newPos, parentTaskId, taskId } = calcDropData({ pos, target });

    handleTaskAction({
      action: TaskAction.MOVE,
      pos: newPos,
      source,
      target: {
        parentTaskId,
        taskId,
      },
    });
  };

  const handleNewTaskClick = (e) => {
    e.stopPropagation();
    handleButtonAction({ action: TaskAction.ADD });
  };

  const handleShowColumnModal = () => {
    setShowColumnEditor(true);
  }

  const handleRowContextMenuEvent = (event, item) => {
    const { target } = event;
    const { nodeName, offsetParent } = target;

    if (nodeName === "TD" || ["TD", "TABLE", "TR"].includes(offsetParent?.offsetParent?.nodeName)) {
      event.preventDefault();
      contextMenu.show({ id: TASK_CONTEXT_MENU_ID, event });
      setContextMenuTask(item);
    }
  };

  const renderTimeReportCell = (enableEdit, item) => {



    return <DurationPopper
      item={item}
      onAdd={handleAddTimeLog}
      onDelete={handleDeleteTimeLog}
      onUpdate={handleUpdateTimeLog}
      enableEdit={enableEdit}
      sx={{ fontSize: "13px", width: "100%" }}
    />
  }

  const handleUpdateTimeLog = (task, reportItem, minutes, date) => {

    //onUpdate(task, reportId, timeToMinutes(time), date);
    onTimeLogAction(TimeLogAction.UPDATE, { task, reportItem, minutes, date });
  }

  const handleAddTimeLog = (task, minutes, date) => {
    onTimeLogAction(TimeLogAction.ADD, { task, minutes, date });
  }

  const handleDeleteTimeLog = (task, timeLogId) => {
    onTimeLogAction(TimeLogAction.DELETE, { task, timeLogId })
  }

  const handleTaskAction = ({ action, task, ...rest }) => {
    if (!onTaskAction) {
      console.warn("** missing onTaskAction");
      return;
    }

    const findPreviousSiblingTaskId = (parentTaskId, taskId) => {
      const siblings = tasks.filter(n => n.parentTaskId === parentTaskId);
      const currentTaskIndex = siblings.findIndex(n => n.id === taskId);
      return siblings[currentTaskIndex - 1]?.id ?? null;
    };

    const { projectId } = project;
    const { pos, source, target, taskname } = rest;

    let data = {};

    switch (action) {
      case TaskAction.INSERT_NEW:

        data = {
          parentTaskId: null,
          insertAfterTaskId: null,
          task: createEmptyTask(projectId, taskname),
        };

        break;
      case TaskAction.ADD:
        switch (pos) {
          case TaskAddPosition.AFTER:
            data = {
              parentTaskId: task.parentTaskId,
              insertAfterTaskId: task.id,
              task: createEmptyTask(projectId),
            };
            break;
          case TaskAddPosition.BEFORE:
            data = {
              parentTaskId: task.parentTaskId,
              insertAfterTaskId: findPreviousSiblingTaskId(task.parentTaskId, task.id),
              task: createEmptyTask(projectId),
            };
            break;
          case TaskAddPosition.SUBTASK:
            data = {
              parentTaskId: task.id,
              insertAfterTaskId: null,
              task: createEmptyTask(projectId),
            };
            break;
          default:
            console.warn("** unhandled parameter", { pos });
            return;
        };
        break;

      case TaskAction.EDIT:
        data = {
          task: removeTreeProperties(task),
          taskId: task.id,
        };
        break;

      case TaskAction.DELETE:
        data = {
          taskId: task.id,
        };
        break;

      case TaskAction.MOVE:
        switch (pos) {
          case TaskAddPosition.AFTER:
            data = {
              parentTaskId: target.parentTaskId,
              insertAfterTaskId: target.taskId,
              taskId: source.taskId,
            };
            break;
          case TaskAddPosition.BEFORE:
            data = {
              parentTaskId: target.parentTaskId,
              insertAfterTaskId: findPreviousSiblingTaskId(target.parentTaskId, target.taskId),
              taskId: source.taskId,
            };
            break;
          case TaskAddPosition.SUBTASK:
            data = {
              parentTaskId: target.taskId,
              insertAfterTaskId: null,
              taskId: source.taskId,
            };
            break;
          default:
            console.warn("** unhandled parameter", { pos });
            return;
        };
        break;

      default:
        console.warn("** unhandled action", { action });
        return;
    }

    onTaskAction({ action, columns, data });
  };

  const removeTreeProperties = (item) => {
    delete item.ancestors;
    delete item.parentNode;
    delete item.treeXLevel;
    delete item.treeYLevel;

    return item;
  };

  const formatDuration = (duration) => {

    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    const formattedHours = String(hours).padStart(2, '0');
    const formattedMinutes = String(minutes).padStart(2, '0');
    return `${formattedHours}:${formattedMinutes}`;
  };

  const handleTaskValueChange = (fieldId, value, taskId) => {
    var field = project.projectFields.find(f => f.fieldId === fieldId);
    var valueField = '';

    switch (field.type) {
      case ColumnTypes.TIME_REPORT: // Duration
      case ColumnTypes.NUMBER:
        valueField = 'numericValue';
        break;

      case ColumnTypes.DATE:
        valueField = 'dateTimeValue';
        break;

      case ColumnTypes.STRING:
      case ColumnTypes.STATUS:
      default:
        valueField = 'stringValue';
        break;
    }

    const task = tasks.find(n => n.id === taskId);
    if (!task) {
      console.warn("** task not found");
      return;
    }
    let updatedTask = cloneDeep(task);
    let updatedField = updatedTask.values.find(n => n.fieldId === fieldId);
    if (updatedField) {
      updatedField[valueField] = value;
    }
    else {
      updatedTask.values = [...updatedTask.values, { fieldId, [valueField]: value }];
    }

    onTaskAction({
      action: TaskAction.UPDATE,
      columns,
      data: {
        task: updatedTask,
      },
    });
  };

  const handleTaskPropertyChange = (name, value, taskId) => {
    const task = tasks.find(n => n.id === taskId);
    if (!task) {
      console.warn("** task not found");
      return;
    }

    let updatedTask = cloneDeep(task);

    updatedTask[name] = value;

    onTaskAction({
      action: TaskAction.UPDATE,
      columns,
      data: {
        task: updatedTask,
      },
    });
  };

  const handleToggleColumnVisibility = (fieldId) => {
    const update = (prev) =>
      prev.includes(fieldId)
        ? prev.filter((id) => id !== fieldId)
        : [...prev, fieldId];

    setVisibleColumnIds(update);
  };

  const hiddenColumnsByIndex = useMemo(() => (
    (columns || []).map((column, _) => !visibleColumnIds.includes(column.fieldId))
  ), [columns, visibleColumnIds]);

  useEffect(() => {
    if (!project) return;

    const previousColumnIds = previousColumnIdsRef.current;

    const columnsList = createColumns(project.projectFields, viewColumnIds);
    const newIds = new Set(columnsList.map(n => n.fieldId));
    const addedIds = Array.from(newIds).filter(id => !previousColumnIds.has(id));

    previousColumnIdsRef.current = newIds;

    setColumns(columnsList);

    setVisibleColumnIds((prev) => {
      const prevSet = new Set(prev);

      const keptIds = prev.filter(id => viewColumnIds.includes(id));
      const missingIds = viewColumnIds.filter(id => !prevSet.has(id));

      const updated = Array.from(new Set([
        ...keptIds,
        ...addedIds,
        ...missingIds,
      ]));

      return updated;
    });
  }, [project, viewColumnIds]);


  useEffect(() => {
    if (Array.isArray(tasks)) {
      const nodesList = createNodes(tasks);
      setNodes(nodesList);
      // set the first task as the context‐menu target
      setContextMenuTask(nodesList[0] ?? null);
    }
  }, [tasks]);

  return (
    <Box component="main" sx={{ flexGrow: 1, p: 0, }}>
      <Box display="flex" alignItems="flex-end" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
        <ProjectButton
          disabled={disabled || !enableEdit}
          label={<FormattedMessage id="project.buttons.addTask" />}
          onClick={handleNewTaskClick}
          startIcon={<AddIcon />}
        />

        <ProjectButton
          disabled={disabled || !enableEdit}
          label={<FormattedMessage id="project.buttons.columns" />}
          onClick={handleShowColumnModal}
          startIcon={<ListIcon />}
        />

        <ColumnVisibilitySelector
          columns={columns}
          onToggleColumn={handleToggleColumnVisibility}
          viewColumnIds={viewColumnIds}
          visibleColumnIds={visibleColumnIds}
        />
      </Box>

      {!disabled && (
        <TaskContextMenu
          contextMenuId={TASK_CONTEXT_MENU_ID}
          enableEdit={enableEdit}
          onAction={handleTaskAction}
          task={contextMenuTask}
        />
      )}

      {showColumnEditor && (
        <ColumnModal
          project={project}
          open={showColumnEditor}
          onClose={(e) => setShowColumnEditor(false)}
          onSave={onProjectUpdate}
        />
      )}

      <ProjectDnDContextProvider>
        <DndProvider backend={HTML5Backend}>
          {columns && (
            <Table
              data={data}
              key={(columns || []).length}
              layout={{ custom: true, horizontalScroll: true }}
              theme={theme}
              tree={tree}
            >
              {(tableList) => (
                <>
                  <Header>
                    <HeaderRow style={{ paddingTop: "7px", paddingBottom: "7px" }}>
                      <HeaderCell resize={resizer} style={{ paddingLeft: "2.4em" }}><FormattedMessage id="project.standardColumn.task" /></HeaderCell>
                      <HeaderCell resize={resizer} hide={!visibleColumnIds.includes(StandardColumnIds.START_DATE)} ><FormattedMessage id="project.standardColumn.startDate" /></HeaderCell>
                      <HeaderCell resize={resizer} hide={!visibleColumnIds.includes(StandardColumnIds.END_DATE)} ><FormattedMessage id="project.standardColumn.endDate" /></HeaderCell>
                      <HeaderCell resize={resizer} hide={!visibleColumnIds.includes(StandardColumnIds.ASSIGNED_TO)} ><FormattedMessage id="project.standardColumn.assigned" /></HeaderCell>
                      <HeaderCell resize={resizer} hide={!visibleColumnIds.includes(StandardColumnIds.TIME_REPORT)} ><FormattedMessage id="project.standardColumn.reportedTime" /></HeaderCell>
                      {columns.map((column, colIndex) => (
                        <HeaderCell hide={hiddenColumnsByIndex[colIndex]} resize={resizer} key={`col_${colIndex}`}>{column.label}</HeaderCell>
                      ))}

                    </HeaderRow>
                  </Header>

                  <Body>
                    {tableList.map((item, _) => (
                      <Row
                        item={item}
                        key={item.id}
                        onContextMenu={(e) => handleRowContextMenuEvent(e, item)}
                      >
                        <CellTreeHoverMore
                          className="celltree"
                          disabled={disabled}
                          draggable={enableEdit}
                          item={item}
                          onDrop={handleDrop}
                          onMoreClick={(e) => handleDetailsButtonClick(item, e)}
                        >
                          <EditorContainer
                            component={StringEditor}
                            disabled={disabled}
                            enableEdit={enableEdit}
                            fullWidth
                            multiline={false}
                            onChange={(newValue) => {
                              if (newValue !== item.name) {
                                handleTaskPropertyChange("name", newValue, item.id);
                              }
                            }}
                            sx={{
                              border: "1px solid rgba(0, 0, 0, 0)",
                              borderRadius: "4px",
                              color: "rgba(0, 0, 0, 0.87)",
                              "&.MuiBox-root": { minHeight: "1.5em", padding: "0px", border: "0px", },
                              "& .MuiTypography-root": { whiteSpace: "pre-wrap", },
                              "&.MuiInputBase-root": { border: "1px solid rgba(0, 0, 0, 0.5)", },
                              "&:hover": {
                                backgroundColor: enableEdit ? "#f0f0f0" : undefined,
                                cursor: enableEdit ? "text" : undefined,
                              },
                            }}
                            value={item.name}
                          />
                        </CellTreeHoverMore>

                        <Cell hide={!visibleColumnIds.includes(StandardColumnIds.START_DATE)}>
                          {
                            enableEdit
                              ? <DateEditor
                                disabled={disabled}
                                enableEdit={enableEdit}
                                onChange={(value) => handleTaskPropertyChange("startDate", value, item.id)}
                                textInputProps={dateEditorTextInputProps}
                                value={item.startDate}
                                views={['year', 'month', 'day']}
                              />
                              : renderDateCellValue({ dateTimeValue: item.startDate })
                          }
                        </Cell>)

                        <Cell hide={!visibleColumnIds.includes(StandardColumnIds.END_DATE)}>
                          {
                            enableEdit
                              ? <DateEditor
                                disabled={disabled}
                                enableEdit={enableEdit}
                                onChange={(value) => handleTaskPropertyChange("endDate", value, item.id)}
                                textInputProps={dateEditorTextInputProps}
                                value={item.endDate}
                                views={['year', 'month', 'day']}
                              />
                              : renderDateCellValue({ dateTimeValue: item.endDate })
                          }
                        </Cell>

                        <Cell hide={!visibleColumnIds.includes(StandardColumnIds.ASSIGNED_TO)}>
                          <UserField
                            disabled={disabled}
                            editable={enableEdit}
                            hideUnassigned
                            name="assignments"
                            onChange={({ name, value }) => {
                              let task = removeTreeProperties(cloneDeep(item));
                              task[name] = value ? [value] : [];
                              onTaskAction({
                                action: TaskAction.UPDATE,
                                columns,
                                data: {
                                  task,
                                },
                              });
                            }}
                            userLookup={userLookup}
                            value={item.assignments ? item.assignments[0] : null}
                          />
                        </Cell>

                        <Cell hide={!visibleColumnIds.includes(StandardColumnIds.TIME_REPORT)}>
                          {
                            renderTimeReportCell(!disabled && enableEdit, item)
                          }
                        </Cell>

                        {columns.map((column, colIndex) => (
                          <Cell key={`col_${colIndex}`} hide={hiddenColumnsByIndex[colIndex]}>
                            {
                              column.renderCell(item, (fieldId, value) => handleTaskValueChange(fieldId, value, item.id), enableEdit, true, disabled/*, (valid) => { } }*/)
                            }
                          </Cell>
                        ))}

                      </Row>
                    ))}
                  </Body>

                  <Footer>
                    <FooterRow>
                      <FooterCell resize={resizer} style={{ marginLeft: "2.4em" }}>
                        <NewTaskRow columns={columns} onAddTask={handleTaskAction} />
                      </FooterCell>
                      <FooterCell resize={resizer} hide={!visibleColumnIds.includes(StandardColumnIds.START_DATE)}></FooterCell>
                      <FooterCell resize={resizer} hide={!visibleColumnIds.includes(StandardColumnIds.END_DATE)}></FooterCell>
                      <FooterCell resize={resizer} hide={!visibleColumnIds.includes(StandardColumnIds.ASSIGNED_TO)}></FooterCell>
                      <FooterCell hide={!visibleColumnIds.includes(StandardColumnIds.TIME_REPORT)}>
                        {
                          formatDuration(tasks.reduce((sum, row) => sum + row.reportedTime, 0))
                        }
                      </FooterCell>
                      {columns.map((column, colIndex) => (
                        <FooterCell resize={resizer} hide={hiddenColumnsByIndex[colIndex]} key={`col_${colIndex}`}></FooterCell>
                      ))}
                    </FooterRow>
                  </Footer>
                </>
              )}
            </Table>
          )}
        </DndProvider>
      </ProjectDnDContextProvider>
    </Box>
  );
};

export default TableView;
